import type { SalesDisplaySlide } from "@/lib/sales-display-slides";
import { cn } from "@/lib/utils";

function isVideo(path: string | null): boolean {
  return /\.(mp4|webm)$/i.test(path ?? "");
}

function Media({ slide, background = false }: { slide: SalesDisplaySlide; background?: boolean }) {
  if (!slide.mediaUrl) return null;
  const className = cn(
    "h-full w-full",
    background ? "absolute inset-0 object-cover" : "object-contain",
  );
  if (slide.kind === "video" || isVideo(slide.mediaPath)) {
    return (
      <video
        src={slide.mediaUrl}
        className={className}
        autoPlay
        muted
        loop
        playsInline
        aria-label={slide.title || "Video"}
      />
    );
  }
  return <img src={slide.mediaUrl} alt={slide.title || "Bilde"} className={className} />;
}

export function ConfiguredSalesDisplaySlide({ slide }: { slide: SalesDisplaySlide }) {
  if (slide.kind === "image" || slide.kind === "video") {
    return (
      <section className="absolute inset-0 bg-black" data-testid="configured-media-slide">
        <Media slide={slide} />
      </section>
    );
  }

  return (
    <section className="absolute inset-0 flex items-center justify-center overflow-hidden bg-[#081F3B] text-white">
      <Media slide={slide} background />
      {slide.mediaUrl && <div className="absolute inset-0 bg-[#081F3B]/65" />}
      <div className="relative z-10 max-w-5xl px-16 text-center">
        <h1 className="font-display text-[clamp(52px,7vw,112px)] font-semibold leading-[0.95] tracking-tight">
          {slide.title}
        </h1>
        {slide.body && (
          <p className="mx-auto mt-8 max-w-4xl whitespace-pre-line text-[clamp(24px,3vw,48px)] leading-tight text-white/90">
            {slide.body}
          </p>
        )}
      </div>
    </section>
  );
}
